import React, { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useNavigate } from 'react-router-dom';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import { createBrandOpsContract, dispatchBrandOpsContract } from '../../../redux/brandOps/actions';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import {
    BRAND_LUXE_MUTED,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    brandModalOutlinedInputSx,
} from '../brandDashboardTokens';
import { generateContractLegalPreview } from './contractLegalPreview';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    MANAGE_PAGE_SX,
    fmtCurrency,
    useBrandManageGate,
} from './manageShared';

// ── Color palette ─────────────────────────────────────────────────────────────
const C_AMBER = '#ffc965';
const C_TEAL = '#00fdc1';
const C_MUTED = '#a0a0a0';
const C_BORDER = 'rgba(255,255,255,0.07)';

const CLEARANCE_OPTIONS = ['organic social', 'paid amplification', 'whitelisting', 'UGC reuse'];

const fieldSx = {
    ...brandModalOutlinedInputSx,
    mb: 2,
};

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({
    icon,
    title,
    subtitle,
    accentColor = C_AMBER,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    accentColor?: string;
    children: React.ReactNode;
}) {
    return (
        <Box
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                border: `1px solid ${C_BORDER}`,
            }}>
            {/* Top accent line */}
            <Box sx={{ height: 2, background: `linear-gradient(90deg, ${accentColor} 0%, transparent 100%)` }} />

            {/* Header */}
            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: `1px solid ${C_BORDER}`,
                    bgcolor: BRAND_SURFACE_CONTAINER,
                }}>
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        bgcolor: `${accentColor}18`,
                        border: `1px solid ${accentColor}2a`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <Box sx={{ color: accentColor, display: 'flex', fontSize: 0 }}>{icon}</Box>
                </Box>
                <Box>
                    <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        {title}
                    </Typography>
                    {subtitle && <Typography sx={{ color: C_MUTED, fontSize: 11, mt: 0.2 }}>{subtitle}</Typography>}
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ p: 2.5 }}>{children}</Box>
        </Box>
    );
}

// ── Scope pill ────────────────────────────────────────────────────────────────
function ScopePill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: 5,
                cursor: 'pointer',
                border: `1px solid ${active ? `${C_AMBER}66` : 'rgba(255,255,255,0.1)'}`,
                bgcolor: active ? `${C_AMBER}14` : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: `${C_AMBER}10`, borderColor: `${C_AMBER}44` },
            }}>
            <Typography
                sx={{
                    fontSize: 12,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    color: active ? C_AMBER : C_MUTED,
                    transition: 'color 0.15s',
                    whiteSpace: 'nowrap',
                }}>
                {label}
            </Typography>
        </Box>
    );
}

export default function BrandManageContractCreatePage() {
    const dispatchP = useDispatchPromise();
    const nav = useNavigate();
    const { brand, brandId } = useBrandManageGate();
    const [fee, setFee] = useState('2500');
    const [currency, setCurrency] = useState('USD');
    const [cadence, setCadence] = useState<'weekly' | 'monthly' | 'per_deliverable'>('monthly');
    const [deliverables, setDeliverables] = useState('12 feed posts + 4 carousels per month');
    const [threshold, setThreshold] = useState('2');
    const [slaMinutes, setSlaMinutes] = useState('');
    const [durationPeriods, setDurationPeriods] = useState('');
    const [scopes, setScopes] = useState<string[]>(['organic social']);
    const [requireApproval, setRequireApproval] = useState(true);
    const [managerEmail, setManagerEmail] = useState('');
    const [managerName, setManagerName] = useState('');
    const [saving, setSaving] = useState(false);

    const preview = useMemo(
        () =>
            generateContractLegalPreview({
                brandName: brand?.name,
                fee: Number(fee) || 0,
                currency,
                cadence,
                deliverables,
                missed_post_threshold: Number(threshold) || 0,
                max_response_minutes: slaMinutes ? Number(slaMinutes) : undefined,
                clearance_scopes: scopes,
                require_brand_approval: requireApproval,
            }),
        [brand?.name, fee, currency, cadence, deliverables, threshold, slaMinutes, scopes, requireApproval],
    );

    const toggleScope = (s: string) => {
        setScopes(prev => (prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]));
    };

    const submit = async (andDispatch: boolean) => {
        if (!brandId || !managerEmail.trim()) {
            showToastMessage('Manager email is required', 'error');
            return;
        }
        setSaving(true);
        try {
            const created: any = await dispatchP(
                createBrandOpsContract({
                    brandId,
                    body: {
                        title: `MSA — ${managerName || managerEmail}`,
                        fee: Number(fee) || 0,
                        currency,
                        cadence,
                        deliverables,
                        missed_post_threshold: Number(threshold) || 0,
                        max_response_minutes: slaMinutes ? Number(slaMinutes) : undefined,
                        clearance_scopes: scopes,
                        require_brand_approval: requireApproval,
                        duration_periods: durationPeriods ? Number(durationPeriods) : undefined,
                        manager_email: managerEmail.trim(),
                        manager_name: managerName.trim() || undefined,
                        legal_preview: preview,
                    },
                }),
            );
            const id = created?._id || created?.id;
            if (andDispatch && id) {
                await dispatchP(dispatchBrandOpsContract({ brandId, contractId: id }));
                showToastMessage('Contract created and dispatched', 'success');
            } else {
                showToastMessage('Contract draft saved', 'success');
            }
            if (id) {
                nav(ROUTES.BRAND_MANAGE_CONTRACT_DETAIL.replace(':contractId', id));
            } else {
                nav(ROUTES.BRAND_MANAGE_CONTRACTS);
            }
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not create contract', 'error');
        } finally {
            setSaving(false);
        }
    };

    const feeNum = Number(fee) || 0;
    const cadenceLabel = cadence === 'weekly' ? 'per week' : cadence === 'monthly' ? 'per month' : 'per deliverable';

    return (
        <BrandManageGate>
            <Box sx={{ ...MANAGE_PAGE_SX, position: 'relative' }}>
                {/* Ambient glow orbs */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -60,
                        left: -60,
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${C_AMBER}0a 0%, transparent 70%)`,
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: -40,
                        width: 240,
                        height: 240,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${C_TEAL}08 0%, transparent 70%)`,
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <ManagePageHeader
                        title="New contract"
                        subtitle="Define compensation, deliverables, and dispatch for signature."
                        back={ROUTES.BRAND_MANAGE_CONTRACTS}
                    />

                    {/* Summary strip */}
                    {feeNum > 0 && (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                borderRadius: 2.5,
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                border: `1px solid ${C_BORDER}`,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 3,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 1.5,
                                        bgcolor: `${C_AMBER}18`,
                                        border: `1px solid ${C_AMBER}2a`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: C_AMBER }} />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: C_MUTED,
                                            fontSize: 10,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.07em',
                                        }}>
                                        Compensation
                                    </Typography>
                                    <Typography sx={{ color: '#fff', fontSize: 15, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                        {fmtCurrency(feeNum, currency)}{' '}
                                        <Box component="span" sx={{ color: C_AMBER, fontSize: 11 }}>
                                            {cadenceLabel}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Box>

                            {managerEmail && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: C_TEAL,
                                            boxShadow: `0 0 6px ${C_TEAL}`,
                                        }}
                                    />
                                    <Typography sx={{ color: '#fff', fontSize: 13 }}>
                                        {managerName || managerEmail}
                                    </Typography>
                                </Box>
                            )}

                            {scopes.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    {scopes.map(s => (
                                        <Box
                                            key={s}
                                            sx={{
                                                px: 1,
                                                py: 0.3,
                                                borderRadius: 1,
                                                bgcolor: `${C_TEAL}12`,
                                                border: `1px solid ${C_TEAL}25`,
                                            }}>
                                            <Typography
                                                sx={{ color: C_TEAL, fontSize: 10, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                                {s}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
                        {/* Left column */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {/* Manager details */}
                            <Section
                                icon={<GavelIcon sx={{ fontSize: 17 }} />}
                                title="Manager details"
                                subtitle="Who is this contract for?"
                                accentColor={C_AMBER}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Manager email"
                                    value={managerEmail}
                                    onChange={e => setManagerEmail(e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Manager name (optional)"
                                    value={managerName}
                                    onChange={e => setManagerName(e.target.value)}
                                    sx={{ ...fieldSx, mb: 0 }}
                                />
                            </Section>

                            {/* Compensation */}
                            <Section
                                icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 17 }} />}
                                title="Compensation & escrow"
                                subtitle="Rate is held in escrow and released at the end of each period"
                                accentColor={C_AMBER}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        size="small"
                                        label="Rate"
                                        type="number"
                                        value={fee}
                                        onChange={e => setFee(e.target.value)}
                                        sx={{ ...brandModalOutlinedInputSx, flex: 1 }}
                                    />
                                    <TextField
                                        select
                                        size="small"
                                        label="Currency"
                                        value={currency}
                                        onChange={e => setCurrency(e.target.value)}
                                        sx={{ ...brandModalOutlinedInputSx, width: 110 }}>
                                        {['USD', 'GBP', 'EUR', 'NGN'].map(c => (
                                            <MenuItem key={c} value={c}>
                                                {c}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Cadence"
                                    value={cadence}
                                    onChange={e =>
                                        setCadence(e.target.value as 'weekly' | 'monthly' | 'per_deliverable')
                                    }
                                    sx={fieldSx}>
                                    {(
                                        [
                                            { value: 'weekly', label: 'Weekly' },
                                            { value: 'monthly', label: 'Monthly' },
                                            { value: 'per_deliverable', label: 'Per deliverable' },
                                        ] as const
                                    ).map(c => (
                                        <MenuItem key={c.value} value={c.value}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Duration (number of periods)"
                                    placeholder="Leave blank for indefinite"
                                    value={durationPeriods}
                                    onChange={e => setDurationPeriods(e.target.value)}
                                    inputProps={{ min: 1 }}
                                    helperText={
                                        durationPeriods
                                            ? `Auto-ends after ${durationPeriods} ${
                                                  cadence === 'weekly'
                                                      ? 'week'
                                                      : cadence === 'monthly'
                                                      ? 'month'
                                                      : 'deliverable'
                                              }${Number(durationPeriods) !== 1 ? 's' : ''}`
                                            : 'Indefinite — runs until manually voided'
                                    }
                                    FormHelperTextProps={{ sx: { color: C_MUTED } }}
                                    sx={{ ...fieldSx, mb: 0 }}
                                />
                            </Section>

                            {/* Deliverables & thresholds */}
                            <Section
                                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 17 }} />}
                                title="Deliverables & thresholds"
                                subtitle="What is the manager expected to deliver?"
                                accentColor={C_TEAL}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={3}
                                    label="Deliverables"
                                    value={deliverables}
                                    onChange={e => setDeliverables(e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Missed post threshold"
                                    value={threshold}
                                    onChange={e => setThreshold(e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="SLA response time (minutes)"
                                    helperText="Max minutes to respond to an inbox DM (optional)"
                                    value={slaMinutes}
                                    onChange={e => setSlaMinutes(e.target.value)}
                                    sx={{ ...fieldSx, mb: 0 }}
                                    FormHelperTextProps={{ sx: { color: BRAND_LUXE_MUTED } }}
                                />
                            </Section>

                            {/* Clearance scopes */}
                            <Section
                                icon={<CheckCircleOutlineIcon sx={{ fontSize: 17 }} />}
                                title="Clearance scopes"
                                subtitle="Which channels and use cases does this contract cover?"
                                accentColor={C_TEAL}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                                    {CLEARANCE_OPTIONS.map(s => (
                                        <ScopePill
                                            key={s}
                                            label={s}
                                            active={scopes.includes(s)}
                                            onClick={() => toggleScope(s)}
                                        />
                                    ))}
                                </Box>
                                <Box
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: BRAND_SURFACE_CONTAINER,
                                        border: `1px solid ${C_BORDER}`,
                                    }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={requireApproval}
                                                onChange={e => setRequireApproval(e.target.checked)}
                                                size="small"
                                                sx={{ color: C_MUTED, '&.Mui-checked': { color: C_AMBER } }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography sx={{ color: '#fff', fontSize: 13 }}>
                                                    Require brand approval
                                                </Typography>
                                                <Typography sx={{ color: C_MUTED, fontSize: 11, mt: 0.1 }}>
                                                    Manager access is gated until you explicitly approve their signature
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Section>

                            {/* CTA */}
                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                    border: `1px solid ${C_BORDER}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}>
                                <Typography
                                    sx={{
                                        color: C_MUTED,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                    }}>
                                    Save or dispatch
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <ManageGoldButton disabled={saving} onClick={() => submit(false)} sx={{ flex: 1 }}>
                                        Save draft
                                    </ManageGoldButton>
                                    <ManageGoldButton disabled={saving} onClick={() => submit(true)} sx={{ flex: 1 }}>
                                        {saving ? 'Saving…' : 'Save & dispatch'}
                                    </ManageGoldButton>
                                </Box>
                                <Typography sx={{ color: C_MUTED, fontSize: 11, textAlign: 'center' }}>
                                    Dispatch sends an email to the manager with a contract review link
                                </Typography>
                            </Box>
                        </Box>

                        {/* Right column — Legal preview */}
                        <Box>
                            <Section
                                icon={<DescriptionOutlinedIcon sx={{ fontSize: 17 }} />}
                                title="Legal preview"
                                subtitle="Auto-generated from your inputs — updates live"
                                accentColor={C_MUTED}>
                                <Box
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        whiteSpace: 'pre-wrap',
                                        fontSize: 11.5,
                                        lineHeight: 1.65,
                                        color: C_MUTED,
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        maxHeight: 680,
                                        overflow: 'auto',
                                        '&::-webkit-scrollbar': { width: 4 },
                                        '&::-webkit-scrollbar-thumb': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            borderRadius: 2,
                                        },
                                    }}>
                                    {preview}
                                </Box>
                            </Section>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </BrandManageGate>
    );
}
